import { Component, computed, inject, input, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BenefitApiService } from '../../../../services/benefit-api.service';
import { ProductType } from './broker-profile.models';

interface HistoryPoint {
  date: string;       // ISO date string
  rate: number;       // percent value (e.g. 9.5 for 9.5%)
  qtrLabel: string;   // "Q1 · 24" or similar
}

interface NormalizedHistory {
  strategyLabel: string;   // e.g. "1-Year PTP with Cap · S&P 500"
  points: HistoryPoint[];
}

/**
 * Rate-history SVG sparkline for the broker profile flyout.
 * Pure SVG (no Highcharts) per mockups 01-fit-tab.html (820×120 wide)
 * and 02-brochure-flyout.html (340×110 narrow).
 *
 * Data source is per product type:
 *   IA   → GetIntHistory (IndexedProfile)
 *   FA   → GetIntHistory (FixedProfile)
 *   RILA → empty state ("Rate history not yet available")
 *   VA   → empty state
 */
@Component({
  selector: 'app-broker-profile-rate-history',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (loading()) {
      <div class="hist">
        <div class="hist-hd">
          <span class="hist-title">Rate history</span>
        </div>
        <div class="hist-empty">Loading…</div>
      </div>
    } @else if (!history() || history()!.points.length < 2) {
      <div class="hist">
        <div class="hist-hd">
          <span class="hist-title">Rate history</span>
        </div>
        <div class="hist-empty">Rate history not yet available for this product.</div>
      </div>
    } @else {
      <div class="hist">
        <div class="hist-hd">
          <span class="hist-title">{{ titleText() }}</span>
          <span class="hist-range">{{ history()!.points.length }} points</span>
        </div>
        <svg class="chart" [attr.viewBox]="viewBox()" preserveAspectRatio="none">
          <!-- Gridlines -->
          @for (y of gridlines(); track y) {
            <line [attr.x1]="0" [attr.y1]="y" [attr.x2]="width()" [attr.y2]="y"
                  stroke="var(--border-light)" stroke-dasharray="2,3"/>
          }
          <!-- Filled area -->
          <path [attr.d]="areaPath()" fill="rgba(26, 95, 166, 0.15)"/>
          <!-- Stroke line -->
          <path [attr.d]="linePath()" fill="none"
                stroke="var(--blue)" [attr.stroke-width]="strokeWidth()" stroke-linejoin="round"/>
          <!-- Data points -->
          @for (pt of renderedPoints(); track $index; let last = $last) {
            @if (last) {
              <circle [attr.cx]="pt.x" [attr.cy]="pt.y" r="4" fill="var(--pos)"
                      stroke="var(--panel)" stroke-width="1.5"/>
            } @else {
              <circle [attr.cx]="pt.x" [attr.cy]="pt.y" [attr.r]="dotRadius()" fill="var(--blue)"/>
            }
          }
          <!-- X-axis labels -->
          @for (lbl of axisLabels(); track $index; let i = $index; let last = $last) {
            <text [attr.x]="lbl.x" [attr.y]="labelY()"
                  [attr.text-anchor]="last ? 'end' : i === 0 ? 'start' : 'middle'"
                  font-family="Roboto Mono" font-size="9"
                  [attr.fill]="last ? 'var(--pos)' : 'var(--label)'"
                  [attr.font-weight]="last ? 700 : 400">{{ lbl.text }}</text>
          }
        </svg>
      </div>
    }
  `,
  styles: [`
    :host { display: block; }

    .hist {
      background: var(--panel);
      border: 1px solid var(--border);
      border-radius: 4px;
      padding: 10px 14px;
      margin-bottom: 14px;
    }
    .hist-hd {
      display: flex;
      align-items: baseline;
      margin-bottom: 6px;
    }
    .hist-title {
      font-family: var(--mono);
      font-size: 10px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.12em;
      text-transform: uppercase;
    }
    .hist-range {
      margin-left: auto;
      font-family: var(--mono);
      font-size: 9px;
      color: var(--label);
      letter-spacing: 0.04em;
    }
    .chart {
      width: 100%;
      display: block;
    }
    .hist-empty {
      font-size: 11px;
      color: var(--muted);
      font-style: italic;
      padding: 12px 0;
    }
  `],
})
export class BrokerProfileRateHistoryComponent {
  private readonly api = inject(BenefitApiService);

  readonly productType = input.required<ProductType>();
  readonly productid = input.required<number>();
  /** 'wide' = Case mode (820×120), 'narrow' = Client-facing (340×110). */
  readonly variant = input<'wide' | 'narrow'>('wide');

  readonly loading = signal(false);
  readonly history = signal<NormalizedHistory | null>(null);

  private readonly hydrateEffect = effect(() => {
    const pid = this.productid();
    const type = this.productType();
    if (!pid) return;
    this.loadHistory(type, pid);
  });

  // Chart geometry
  readonly width = computed(() => this.variant() === 'wide' ? 820 : 340);
  readonly chartHeight = computed(() => this.variant() === 'wide' ? 120 : 110);
  readonly viewBox = computed(() => `0 0 ${this.width()} ${this.chartHeight()}`);
  readonly strokeWidth = computed(() => this.variant() === 'wide' ? 2.25 : 2);
  readonly dotRadius = computed(() => this.variant() === 'wide' ? 3 : 2.5);
  readonly labelY = computed(() => this.chartHeight() - 3);

  /** Three evenly-spaced horizontal gridlines. */
  readonly gridlines = computed<number[]>(() => {
    const h = this.chartHeight();
    const plotH = h - 25;  // leave room for labels
    return [plotH * 0.25, plotH * 0.55, plotH * 0.85];
  });

  private readonly plotTop = 10;
  private readonly plotBottomOffset = 25;

  /** Data points projected into SVG coordinates. */
  readonly renderedPoints = computed<{ x: number; y: number; rate: number; qtrLabel: string }[]>(() => {
    const h = this.history();
    if (!h || h.points.length === 0) return [];
    const rates = h.points.map(p => p.rate);
    const min = Math.min(...rates);
    const max = Math.max(...rates);
    const range = max - min || 1;  // avoid divide-by-zero for flat series
    const w = this.width();
    const chartH = this.chartHeight();
    const plotH = chartH - this.plotBottomOffset - this.plotTop;
    const step = h.points.length > 1 ? (w - 40) / (h.points.length - 1) : 0;
    return h.points.map((p, i) => ({
      x: 20 + i * step,
      y: this.plotTop + plotH - ((p.rate - min) / range) * plotH,
      rate: p.rate,
      qtrLabel: p.qtrLabel,
    }));
  });

  /** Bottom-closed polygon path for the filled area. */
  readonly areaPath = computed<string>(() => {
    const pts = this.renderedPoints();
    if (pts.length === 0) return '';
    const baseline = this.chartHeight() - this.plotBottomOffset;
    const top = pts.map((p, i) => (i === 0 ? 'M' : 'L') + ` ${p.x} ${p.y}`).join(' ');
    const last = pts[pts.length - 1];
    const first = pts[0];
    return `${top} L ${last.x} ${baseline} L ${first.x} ${baseline} Z`;
  });

  /** Open stroke path for the line on top of the area. */
  readonly linePath = computed<string>(() => {
    const pts = this.renderedPoints();
    if (pts.length === 0) return '';
    return pts.map((p, i) => (i === 0 ? 'M' : 'L') + ` ${p.x} ${p.y}`).join(' ');
  });

  /** Date labels under each data point. */
  readonly axisLabels = computed<{ x: number; text: string }[]>(() => {
    return this.renderedPoints().map(p => ({ x: p.x, text: p.qtrLabel }));
  });

  readonly titleText = computed<string>(() => {
    const h = this.history();
    return h ? `${h.strategyLabel}` : 'Rate history';
  });

  private loadHistory(type: ProductType, pid: number): void {
    this.loading.set(true);
    this.history.set(null);
    if (type === 'ia') {
      this.api.getIAIntHistory(pid).subscribe({
        next: (res) => {
          const rows = Array.isArray(res) ? res : (res?.data ?? []);
          this.history.set(this.normalizeIa(rows));
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
    } else if (type === 'fa') {
      this.api.getFixedIntHistory(pid).subscribe({
        next: (res) => {
          const rows = Array.isArray(res) ? res : (res?.data ?? []);
          this.history.set(this.normalizeFa(rows));
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
    } else {
      // RILA / VA have no history endpoint today
      this.loading.set(false);
    }
  }

  /** Group IA history by stratid, pick the primary strategy, extract 8 most
   *  recent points of whichever rate field is populated. */
  private normalizeIa(rows: any[]): NormalizedHistory | null {
    if (!rows?.length) return null;
    const byStrat = new Map<number, any[]>();
    for (const r of rows) {
      if (!byStrat.has(r.stratid)) byStrat.set(r.stratid, []);
      byStrat.get(r.stratid)!.push(r);
    }
    // Pick the stratid with the most rows (proxy for primary strategy).
    let primary: any[] | null = null;
    let primaryStratMeta: any = null;
    for (const rows of byStrat.values()) {
      if (!primary || rows.length > primary.length) {
        primary = rows;
        primaryStratMeta = rows[0];
      }
    }
    if (!primary || primary.length === 0) return null;

    // Pick the rate column with data (first row's populated one wins).
    const rateKey = (['curcap', 'curparrat', 'curspread', 'fixedrate', 'currpertrig'] as const)
      .find(k => primary!.some(r => r[k] != null));
    if (!rateKey) return null;

    const points = primary
      .filter(r => r[rateKey] != null && r.incepdate)
      .sort((a, b) => a.incepdate.localeCompare(b.incepdate))
      .slice(-8)
      .map(r => ({
        date: r.incepdate as string,
        rate: Number(r[rateKey]) * 100,  // IA values are decimals
        qtrLabel: this.quarterLabel(r.incepdate),
      }));

    const strategyLabel = `${primaryStratMeta.credstrat?.trim() || primaryStratMeta.mktgname?.trim() || 'Strategy'} · ${primaryStratMeta.indexused?.trim() || '—'}`;
    return { strategyLabel, points };
  }

  /** Group FA history by intrateter (term) band, pick first term's band,
   *  extract effrate over time. */
  private normalizeFa(rows: any[]): NormalizedHistory | null {
    if (!rows?.length) return null;
    // FA bands are keyed by (intrateter, bandid). Pick the most common intrateter
    // first, then pick the band within that term with the most rows.
    const byTerm = new Map<number, any[]>();
    for (const r of rows) {
      if (!byTerm.has(r.intrateter)) byTerm.set(r.intrateter, []);
      byTerm.get(r.intrateter)!.push(r);
    }
    let primaryTerm: any[] | null = null;
    for (const arr of byTerm.values()) {
      if (!primaryTerm || arr.length > primaryTerm.length) primaryTerm = arr;
    }
    if (!primaryTerm) return null;

    // Within that term, pick first band (lowest minimumcon) for stability.
    const byBand = new Map<number, any[]>();
    for (const r of primaryTerm) {
      if (!byBand.has(r.bandid)) byBand.set(r.bandid, []);
      byBand.get(r.bandid)!.push(r);
    }
    const bands = [...byBand.values()].sort((a, b) => (a[0]?.minimumcon ?? 0) - (b[0]?.minimumcon ?? 0));
    const primary = bands[0];
    if (!primary || primary.length === 0) return null;

    const points = primary
      .filter(r => r.effrate != null && r.begindate)
      .sort((a, b) => a.begindate.localeCompare(b.begindate))
      .slice(-8)
      .map(r => ({
        date: r.begindate as string,
        rate: Number(r.effrate),  // FA effrate is literal percent
        qtrLabel: this.quarterLabel(r.begindate),
      }));

    const strategyLabel = `${primary[0].intrateter}yr MYGA · Band ${primary[0].minimumcon?.toLocaleString() || '—'}+`;
    return { strategyLabel, points };
  }

  /** ISO date → "Q1 · 25" quarterly label. */
  private quarterLabel(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    const q = Math.floor(d.getUTCMonth() / 3) + 1;
    const yr = String(d.getUTCFullYear()).slice(-2);
    return `Q${q}·${yr}`;
  }
}
